/**
 * Connected Clients Panel
 * Shows currently connected mail clients (from Dovecot)
 */
import React from 'react';
import {
  ComputerDesktopIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import type { ConnectedClient } from '../../types/admin.types';

interface ConnectedClientsPanelProps {
  clients: ConnectedClient[];
  isLoading: boolean;
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

const protocolBadge = (protocol: string) => {
  const styles: Record<string, string> = {
    imap: 'bg-lex-accent-soft text-lex-accent',
    lmtp: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    pop3: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  };
  return styles[protocol.toLowerCase()] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
};

export const ConnectedClientsPanel: React.FC<ConnectedClientsPanelProps> = ({ clients, isLoading }) => {
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm animate-pulse">
        <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-lex-accent-soft">
            <ComputerDesktopIcon className="h-5 w-5 text-lex-accent" />
          </div>
          <span className="font-medium text-gray-900 dark:text-white">Connected Clients</span>
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400">{clients.length} active</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <th className="px-4 py-3">Client</th>
              <th className="px-4 py-3">Protocol</th>
              <th className="px-4 py-3">Mailbox</th>
              <th className="px-4 py-3">IP Address</th>
              <th className="px-4 py-3 text-right">Connections</th>
              <th className="px-4 py-3 text-right">Last Seen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {clients.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-600 dark:text-gray-400">
                  <GlobeAltIcon className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                  No connected clients
                </td>
              </tr>
            ) : (
              clients.map((client) => (
                <tr key={client.clientId} className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{client.clientName}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium uppercase ${protocolBadge(client.protocol)}`}>
                      {client.protocol}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{client.mailbox}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-600 dark:text-gray-400">{client.ipAddress}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 text-right">{client.connectionCount}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-right">{formatTimeAgo(client.lastSeen)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ConnectedClientsPanel;
